import React, { useCallback, useMemo } from 'react'

import { runInAction } from 'mobx'
import { inject, observer } from 'mobx-react'

import { useOverlayNotifier } from '~/client/src/shared/utils/overlayNotifier'

import Workflow from '../../models/Workflow'
import WorkflowTypesStore from '../../stores/domain/WorkflowTypes.store'
import { SAVE_WORKFLOWS } from '../../stores/EventStore/eventConstants'
import EventsStore from '../../stores/EventStore/Events.store'
import BsonObjectId from '../../utils/BsonObjectId'
import { advanceToNextWorkflowStep } from '../../utils/workflowStepUtils'
import { ListSelector } from '../ListSelector'
import IWorkflowCreationParent from '../WorkflowDirectory/IWorkflowCreationParent'
import WorkflowDirectory from '../WorkflowDirectory/WorkflowDirectory'

import './LinkedWorkflowPicker.scss'

interface IProps {
  entity: Workflow
  selectedWorkflowIds: string[]
  title: string
  onClose: () => void
  eventsStore?: EventsStore
  workflowTypesStore?: WorkflowTypesStore
}

const LinkedWorkflowPicker: React.FC<IProps> = observer(
  ({
    entity,
    selectedWorkflowIds,
    title,
    onClose,
    eventsStore,
    workflowTypesStore,
  }) => {
    useOverlayNotifier(true)

    const parentStoreAdapter: IWorkflowCreationParent = useMemo(() => {
      return {
        get editablePermit() {
          return entity
        },
        get template() {
          return workflowTypesStore.getPermitTypeById(entity.typeId)
        },
        changeSelectedFieldValue: <T,>(newValue: T) => {
          const newPermit = newValue as unknown as Workflow
          const newId = BsonObjectId.generate()
          newPermit.setId(newId)

          const permitType = workflowTypesStore.getPermitTypeById(
            newPermit.typeId,
          )
          if (permitType) {
            advanceToNextWorkflowStep(
              newPermit,
              permitType.stepsWithoutRecurring,
            )
          }

          // Save immediately — WorkflowDirectory's Save button won't enable
          // since the list selection didn't change from its perspective.
          runInAction(() => {
            entity.linkedWorkflows = [...(entity.linkedWorkflows || []), newId]
          })
          eventsStore.dispatch(SAVE_WORKFLOWS, [newPermit, entity])
          onClose()
        },
      }
    }, [entity, eventsStore, workflowTypesStore, onClose])

    const handleSave = useCallback(
      (ids: string[]) => {
        runInAction(() => {
          entity.linkedWorkflows = ids
        })
        eventsStore.dispatch(SAVE_WORKFLOWS, [entity])
        onClose()
      },
      [entity, eventsStore, onClose],
    )

    const workflowPredicate = useCallback(
      (workflow: Workflow) => workflow.id !== entity.id,
      [entity.id],
    )

    return (
      <>
        <div className="linked-workflow-picker-overlay" onClick={onClose} />
        <div className="linked-workflow-picker-sheet col">
          <ListSelector.Title title={title} onClose={onClose} />
          <WorkflowDirectory
            parentStore={parentStoreAdapter}
            workflowPredicate={workflowPredicate}
            selectedWorkflowIds={selectedWorkflowIds}
            onSave={handleSave}
            onCancel={onClose}
            allowSelectExisting={true}
            allowCreateNew={true}
            showQuickFilters={true}
          />
        </div>
      </>
    )
  },
)

export default inject('eventsStore', 'workflowTypesStore')(LinkedWorkflowPicker)
